/**
 * Authed fetch wrapper for the same-origin /api/*. Attaches the PKCE-issued
 * access token as `Authorization: Bearer <token>`; a 401 clears the token
 * and bounces the user back through the OIDC authorize flow.
 */

import { API_BASE } from "../config.ts";
import { beginLogin, clearToken, getToken } from "./auth.ts";

export class UnauthorizedError extends Error {
	constructor() {
		super("Unauthorized");
		this.name = "UnauthorizedError";
	}
}

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
	const token = getToken();
	const headers: Record<string, string> = {
		Accept: "application/json",
		...(init?.headers as Record<string, string> | undefined),
	};
	if (token) headers.Authorization = `Bearer ${token}`;
	const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
	if (res.status === 401) {
		clearToken();
		void beginLogin(window.location.pathname + window.location.search);
		throw new UnauthorizedError();
	}
	return res;
}

async function body<T>(res: Response, what: string): Promise<T> {
	const parsed = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error((parsed as { error?: string }).error ?? `${what} (${res.status})`);
	return parsed as T;
}

const getJson = async <T>(path: string) => body<T>(await authFetch(path), `GET ${path} failed`);

const send = async <T>(method: string, path: string, payload?: unknown) =>
	body<T>(
		await authFetch(path, {
			method,
			headers: { "Content-Type": "application/json" },
			body: payload === undefined ? undefined : JSON.stringify(payload),
		}),
		`${method} ${path} failed`,
	);

// ── Shapes ───────────────────────────────────────────────────────────

export interface Me {
	id: string;
	email?: string;
	role?: string;
	appAccess?: string[];
}

// The ladder lives in ./roles.ts so a test runner can load it — api.ts imports
// config.ts, which reads import.meta.env, and node --test has no such thing.
// Imported for the shapes below and re-exported so callers keep one import site.
import type { Role } from "./roles.ts";
export type { Role };
export { roleAtLeast } from "./roles.ts";
export type EntityType = "agents" | "fb_accounts" | "proxies";
export type Permission = "read" | "write";

export interface Grant {
	entityType: EntityType;
	entityId: string;
	permission: Permission;
}

export interface MyAccess {
	userId: string;
	email: string | null;
	role: Role;
	/** True when the role comes from the platform, not a seated member row. */
	inherited: boolean;
	status: string;
	grants: Grant[];
	canAssign: Role[];
}

export interface Member {
	id: string;
	userId: string;
	email: string | null;
	displayName: string | null;
	role: Role;
	status: "active" | "suspended";
	updatedAt: string;
	grants: Grant[];
}

export interface Agent {
	id: string;
	name: string;
	kind: string;
	description: string | null;
	status: string;
}

export interface ProxyRow {
	id: string;
	label: string;
	protocol: string;
	host: string;
	port: number;
	country: string | null;
	credRef: string | null;
	status: string;
}

export interface FbAccount {
	id: string;
	metaAccountId: string;
	name: string;
	currency: string | null;
	accountStatus: number | null;
	spendCap: string | null;
	/**
	 * Not optional any more, and the change is the point.
	 *
	 * While the worker did not send this field, `absent` meant "not deployed yet"
	 * and `spendPosition` stayed silent about it — a caution on every capped
	 * account over a number nobody had sent would have been a caution nobody
	 * earned. `#75` landed the worker half, and the store now sets it on every
	 * row, null or a string.
	 *
	 * So the `?` goes, exactly as its own comment said it would: `undefined`
	 * becomes unreachable at the type level, and the branch that handled it had
	 * to be reopened and decided again rather than left meaning something it no
	 * longer means.
	 */
	amountSpent: string | null;
	status: string;
	syncedAt: string | null;
}

export type OrgRole = "owner" | "manager" | "member";

export interface Team {
	id: string;
	name: string;
	slug: string;
	status: "pending" | "active" | "suspended" | "rejected";
	requestedBy: string;
	note: string | null;
	memberCount?: number;
	grants?: Grant[];
}

export interface TeamMember {
	id: string;
	userId: string;
	email: string | null;
	role: OrgRole;
	status: string;
}

/** What the console renders for this caller — see lib/principal.ts. */
export interface Standing {
	userId: string;
	email: string | null;
	standing: "platform" | "tenant" | "pending" | "unaffiliated";
	platform: { role: Role; inherited: boolean } | null;
	team: {
		id: string;
		name: string;
		status: Team["status"];
		role: OrgRole;
		grants: Grant[];
	} | null;
}

/** One dependency check. `target` names the exact database/dataset reached. */
export interface Check {
	state: "ok" | "error" | "skipped";
	target: string;
	detail?: string;
}

export interface Smoke {
	db?: Check;
	bq?: Check;
	meta?: Check;
}

export interface ColumnInfo {
	name: string;
	type: string;
	nullable: boolean;
	description: string | null;
}

export interface IndexInfo {
	name: string;
	definition: string;
	unique: boolean;
	primary: boolean;
}

export interface ConstraintInfo {
	name: string;
	definition: string;
	kind: "check" | "foreign key";
}

export interface TableInfo {
	name: string;
	description: string | null;
	rows: number | null;
	columns: ColumnInfo[];
	/** Always present, `[]` for BigQuery. Worker script and Static Assets go up
	 *  in one `wrangler deploy`, so this SPA never meets a worker that does not
	 *  send it. */
	indexes: IndexInfo[];
	constraints: ConstraintInfo[];
}

export interface StoreSchema {
	label: string;
	tables: TableInfo[];
	error?: string;
}

export interface Schema {
	cloudsql: StoreSchema;
	bigquery: StoreSchema;
}

/**
 * One line of the append-only trail.
 *
 * Snake_case on purpose, and against this project's own convention: the audit
 * endpoint returns raw Postgres rows rather than mapping them the way every
 * other store function does, so `actor_id` and `created_at` are what actually
 * arrives on the wire. Typing it honestly here beats a camelCase interface that
 * would be quietly wrong at runtime. If the endpoint is ever fixed to match
 * CLAUDE.md, this interface is where it shows.
 */
export interface AuditEntry {
	actor_id: string;
	action: string;
	subject_id: string | null;
	detail: Record<string, unknown> | null;
	created_at: string;
}

// ── Calls ────────────────────────────────────────────────────────────

export const api = {
	me: () => getJson<Me>("/api/me"),
	smoke: () => getJson<Smoke>("/api/v1/smoke"),

	standing: () => getJson<Standing>("/api/v1/orgs/me"),
	registerTeam: (payload: { name: string; note?: string }) =>
		send<{ team: Team }>("POST", "/api/v1/orgs", payload),
	teams: () =>
		getJson<{ teams: Team[]; accountHolders: Record<string, { orgId: string; orgName: string }> }>(
			"/api/v1/orgs",
		),
	setTeamStatus: (orgId: string, status: Team["status"]) =>
		send<{ team: Team }>("PUT", `/api/v1/orgs/${orgId}/status`, { status }),
	setTeamGrants: (
		orgId: string,
		payload: { entity_type: EntityType; permission: Permission; entity_ids: string[] },
	) => send<{ ok: true }>("PUT", `/api/v1/orgs/${orgId}/grants`, payload),
	teamMembers: (orgId: string) =>
		getJson<{ members: TeamMember[] }>(`/api/v1/orgs/${orgId}/members`),
	setTeamMember: (orgId: string, userId: string, payload: { role: OrgRole; email?: string }) =>
		send<{ member: TeamMember }>("PUT", `/api/v1/orgs/${orgId}/members/${userId}`, payload),
	removeTeamMember: (orgId: string, userId: string) =>
		send<{ ok: true }>("DELETE", `/api/v1/orgs/${orgId}/members/${userId}`),

	myAccess: () => getJson<MyAccess>("/api/v1/access/me"),
	members: () => getJson<{ members: Member[] }>("/api/v1/access/members"),
	setMember: (userId: string, payload: { role: Role; status?: string; email?: string }) =>
		send<{ member: Member }>(
			"PUT",
			`/api/v1/access/members/${encodeURIComponent(userId)}`,
			payload,
		),
	removeMember: (userId: string) =>
		send<{ ok: true }>("DELETE", `/api/v1/access/members/${encodeURIComponent(userId)}`),
	/** Replaces the whole set for one entity type + permission. */
	setGrants: (
		userId: string,
		payload: { entity_type: EntityType; permission: Permission; entity_ids: string[] },
	) =>
		send<{ member: Member }>(
			"PUT",
			`/api/v1/access/members/${encodeURIComponent(userId)}/grants`,
			payload,
		),

	/*
	 * `truncated` is required on all three, and on `/api/v1/accounts` below.
	 *
	 * Not optional-because-the-server-might-not-send-it: every one of those
	 * endpoints returns it unconditionally, including the platform-admin branch
	 * that reads no mirror and reports `false` anyway. And the worker script and
	 * the Static Assets go up in a single `wrangler deploy`, so this SPA never
	 * meets a worker that predates the field.
	 *
	 * Written here rather than only in the commit that removed the `?`, because
	 * that is the mistake this line exists to stop: the same `?` was taken out of
	 * `TableInfo.indexes` for this same reason and reappeared here two diffs
	 * later. A guard held up by its explanation comes back whenever the
	 * explanation sounds prudent again — so the refutation has to live where the
	 * guard lived, not in the history.
	 */
	agents: () => getJson<{ agents: Agent[]; truncated: boolean }>("/api/v1/agents"),
	createAgent: (payload: { name: string; kind?: string; description?: string }) =>
		send<{ agent: Agent }>("POST", "/api/v1/agents", payload),
	updateAgent: (id: string, payload: { name?: string; kind?: string; status?: string }) =>
		send<{ agent: Agent }>("PATCH", `/api/v1/agents/${id}`, payload),
	deleteAgent: (id: string) => send<{ ok: true }>("DELETE", `/api/v1/agents/${id}`),

	proxies: () => getJson<{ proxies: ProxyRow[]; truncated: boolean }>("/api/v1/proxies"),
	createProxy: (payload: {
		label: string;
		protocol?: string;
		host: string;
		port: number;
		country?: string;
		cred_ref?: string;
	}) => send<{ proxy: ProxyRow }>("POST", "/api/v1/proxies", payload),
	updateProxy: (
		id: string,
		payload: { label?: string; host?: string; port?: number; country?: string; status?: string },
	) => send<{ proxy: ProxyRow }>("PATCH", `/api/v1/proxies/${id}`, payload),
	deleteProxy: (id: string) => send<{ ok: true }>("DELETE", `/api/v1/proxies/${id}`),

	fbAccounts: () => getJson<{ accounts: FbAccount[]; truncated: boolean }>("/api/v1/fb-accounts"),
	syncFbAccounts: () => send<{ synced: number }>("POST", "/api/v1/fb-accounts/sync"),

	schema: () => getJson<Schema>("/api/v1/system/schema"),
	audit: () => getJson<{ entries: AuditEntry[] }>("/api/v1/access/audit"),
};
